<template>
  <transition-group tag="div" name="list" class="notifications">
    <div
      v-for="[groupName, group] in Object.entries(notificationGroups)"
      :key="groupName"
      class="group"
      :style="{ marginBottom: `${14 + (Object.keys(group).length - 1) * 10}px` }"
    >
      <transition-group name="fan">
        <div
          v-for="(notification, idx) in Object.values(group)"
          :key="notification.id"
          class="notification-transformer"
          :style="notificationStyle(group, idx)"
        >
          <notification-component
            :title="notification.title"
            :variant="notification.variant"
            @close="close(groupName, notification.id, notification.onClose)"
          />
        </div>
      </transition-group>
    </div>
  </transition-group>

  <slot />
</template>

<script lang="tsx" setup>
import { provide, reactive, type Component } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { notifyKey, type NotificationOptions } from './injects';
import NotificationComponent, { type NotificationVariant } from './Notification.vue';

export interface Notification {
  id: string;
  title: string | JSX.Element | Component;
  variant: NotificationVariant;
  onClose?: () => void;
}

const notificationGroups = reactive<Record<string, Notification[]>>({});

const close = (group: string, id: string, cb?: () => void) => {
  if (!(group in notificationGroups)) return;
  const idx = notificationGroups[group].findIndex(({ id: nId }) => nId === id);
  if (idx === -1) return;
  notificationGroups[group].splice(idx, 1);
  if (Object.keys(notificationGroups[group]).length === 0) {
    delete notificationGroups[group];
  }
  if (cb) cb();
};

const notify = (notification: NotificationOptions) => {
  const groupName = notification.group ?? 'default';
  if (!notificationGroups[groupName]) notificationGroups[groupName] = [];
  const group = notificationGroups[groupName];

  const id = uuidv4();
  const closeCb = notification.onClose;
  const closeLater = !notification.duration || notification.duration > 0;
  if (closeLater) {
    setTimeout(() => close(groupName, id, closeCb), notification.duration || 3000);
  }

  group.unshift({
    id,
    title: notification.title,
    variant: notification.variant,
    onClose: closeCb,
  });
  
  return {
    dismiss: () => close(groupName, id, closeCb)
  }
};

const notificationStyle = (group: Notification[], notificationIndex: number) => {
  const items = group.length;
  const inverseIndex = items - notificationIndex - 1;
  const scale = 1 - inverseIndex * 0.05;
  if (inverseIndex === 0) return {};
  const t = inverseIndex * 10;
  return {
    transform: `translateY(${t}px) scale(${scale}) rotate3d(1, 0, 0, ${inverseIndex * 5}deg)`,
  };
};

provide(notifyKey, notify);
</script>

<style lang="scss" scoped>
.notifications {
  @apply relative;
  @apply fixed top-10 right-10;
  z-index: 100000;
  min-width: px(400);

  & > * {
    @apply mb-3.5;

    &:last-child {
      @apply mb-0;
    }
  }

  .group {
    @apply relative;
    @apply right-0 w-full;
    min-width: max-content;

    &.list-leave-active {
      @apply absolute;
    }

    & > * {
      @apply absolute top-0 right-0 w-full;
      @apply flex justify-end;

      transition: all 0.5s ease;

      &:last-child {
        @apply relative transition-none;
      }
    }

    perspective: 20rem;
    perspective-origin: center;
    transform-origin: 50% 100%;
  }
}

.fan-enter-active,
.fan-leave-active {
  transition: all 0.5s ease !important;
  pointer-events: none;
}
.fan-enter-from,
.fan-leave-to {
  opacity: 0;
  transform: translateX(2rem) !important;
}
</style>
